import { useQuery } from '@tanstack/react-query';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { ActivityFormSchema } from '@/constants/formSchema/ActivityFormSchema';
import { TrainingTypeEnum } from '@/constants/trainingTypeOptions';
import api from '@/services/apiSgft';
import { EmployeeTypeEnum } from '@/types/ActivityData';
import { Option } from '@/types/Filter';

import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import TrashButton from '../atoms/TrashButton';
import ActivityPoleConditionTraining from './ActivityPoleConditionTraining';

const trainingTypeOptions = [
	{
		value: TrainingTypeEnum.REGULATORY,
		label: TrainingTypeEnum.REGULATORY,
		disabled: false,
	},
	{
		value: TrainingTypeEnum.PROCEDURE,
		label: TrainingTypeEnum.PROCEDURE,
		disabled: false,
	},
];
interface ActivityPoleConditionProps {
	conditionIndex: number;
	poleIndex: number;
	poleId: string;
	conditionId: string;
	form: UseFormReturn<z.infer<typeof ActivityFormSchema>>;
	removeCondition: (index: number) => void;
	employeeType: EmployeeTypeEnum;
}

export const ActivityPoleCondition = ({
	conditionIndex,
	poleIndex,
	poleId,
	conditionId,
	form,
	removeCondition,
	employeeType,
}: ActivityPoleConditionProps) => {
	const baseName =
		employeeType === EmployeeTypeEnum.INTERNAL
			? `internalData.activityPoles`
			: `thirdPartyData.activityPoles`;
	const control = form.control;

	const {
		fields: trainingFields,
		append: appendTraining,
		remove: removeTraining,
	} = useFieldArray({
		control,
		name: `${baseName}.${poleIndex}.conditions.${conditionIndex}.trainings`,
	});
	const trainingTypeWatch = form.watch(
		`${baseName}.${poleIndex}.conditions.${conditionIndex}.trainingType`,
	);
	const getTrainings = async () => {
		try {
			const response = await api.get('/trainings', {
				params: {
					getAll: false,
					trainingType: [trainingTypeWatch],
				},
			});
			return response.data.map((el: { id: number; name: string }) => ({
				value: el.id,
				label: el.name,
			}));
		} catch (e) {
			toast.error('Erro ao carregar treinamentos!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const { data: trainings } = useQuery<Option[]>(
		['get-all-trainings', trainingTypeWatch],
		getTrainings,
		{
			enabled: !!trainingTypeWatch,
		},
	);

	return (
		<div className="flex w-full  gap-2 rounded-md bg-gray-200 p-3">
			<div className=" flex w-full items-start gap-2">
				<div className="flex w-full items-center justify-between">
					<ControlledSimpleSelect
						title="Tipo de Treinamento"
						width="12rem"
						required
						values={trainingTypeOptions}
						control={control}
						{...form.register(
							`${baseName}.${poleIndex}.conditions.${conditionIndex}.trainingType`,
						)}
					/>
				</div>

				<div className="flex flex-col items-end gap-2">
					<div className="flex flex-col gap-2">
						{trainingFields.map((training, trainingIndex) => {
							return (
								<div
									key={`training-${poleId}-${conditionId}-${training.id}`}
									className="flex items-center gap-2"
								>
									<ActivityPoleConditionTraining
										poleIndex={poleIndex}
										conditionIndex={conditionIndex}
										trainingIndex={trainingIndex}
										employeeType={employeeType}
										form={form}
										trainings={trainings ?? []}
										removeTraining={() => removeTraining(trainingIndex)}
									/>
								</div>
							);
						})}
					</div>

					<button
						className="w-56 text-xs font-medium text-primary hover:opacity-80"
						onClick={() =>
							appendTraining({
								trainingId: null,
							})
						}
						type="button"
					>
						+ Adicionar Treinamento Equivalente
					</button>
				</div>
			</div>

			<TrashButton
				onClick={() => removeCondition(conditionIndex)}
				color="action"
				className="self-end"
			/>
		</div>
	);
};
